/**
 * One blueprint (T2.12, T2.13).
 *
 * Loads the blueprint's metadata and one version's model, then renders the
 * ported Blueprint view inside a TermsProvider carrying that blueprint's
 * RESOLVED terminology. Everything below this point therefore gets the client's
 * own vocabulary from useTerms() without a second round trip — which is why
 * T2.10a asks the API to return it inline.
 *
 * Which version is shown is a URL search parameter, so a version is linkable and
 * a reload keeps it. That also means a viewer can paste a draft's id, which is
 * precisely why the API answers 403 for that and this screen renders the refusal
 * rather than silently substituting the latest published version.
 *
 * T9.1-T9.6 — THE WEEK PREVIEW. A second search parameter, `?week=YYYY-MM-DD`,
 * renders the blueprint as it was in that week. It is a PREVIEW layered over
 * whatever is open, not a navigation:
 *
 *   - the open version's query stays mounted and enabled throughout, and so
 *     does `useDraftEditor` with it, so an editor's draft is still there,
 *     untouched, when they come back. Nothing is prompted and nothing is
 *     discarded — the decision Werner settled on 8 Oct (design.md § 10c).
 *   - the previewed week's version is a SECOND payload query. Two sources, one
 *     of which is temporary; the route renders the temporary one and keeps the
 *     other loaded underneath.
 *   - every editing control is withheld while previewing — absent from the DOM,
 *     not disabled, like every other read-only control here.
 *   - both parameters live in the URL, which stays the single source of truth
 *     for what is on screen. That is what makes a previewed week shareable and
 *     the Back button behave, and it is what stops the version dropdown and the
 *     slider from disagreeing about what is being rendered.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useParams } from 'react-router';
import { TermsProvider, useTerms } from '../lib/terms';
import { useDocumentTitle } from '../lib/documentTitle';
import { ApiError } from '../lib/api';
import {
  canEdit,
  payloadKey,
  useBlueprint,
  useVersionPayload,
  type BlueprintDetail,
  type PayloadSource,
  type VersionPayload,
} from '../lib/blueprints';
import { BlueprintView } from '../features/blueprint/BlueprintView';
import { weekLabel } from '../features/blueprint/ProgressBar';
import { mondayOf, ymd } from '../features/blueprint/weeks';
import { indexOfWeek, useWeekSeries, weeksToDate, type WeekSeriesRow } from '../lib/weekSeries';
import { TimelineView } from '../features/timeline/TimelineView';
import { DocumentsView } from '../features/documents/DocumentsView';
import { ViewTabs } from '../lib/viewTabs';
import { useTheme } from '../lib/theme';
import { useDraftEditor, type MovedOn } from '../features/blueprint/useDraftEditor';
import { VersionSelector, versionLabel } from '../features/versions/VersionSelector';
import { stateTermKey } from '../features/blueprint/stateTerms';
import { PublishButton } from '../features/versions/PublishButton';
import { UploadDialog } from '../features/versions/UploadDialog';
import { DownloadButton } from '../features/versions/DownloadButton';

/**
 * The status line.
 *
 * CLAUDE.md: this stays on ONE line. If it wraps, the page height changes and
 * the page shifts under the cursor mid-click — a real defect that was found and
 * fixed once already in the generator. `.statusline` pins it with
 * `white-space:nowrap` and an ellipsis; do not remove either.
 */
function StatusLine({
  detail,
  payload,
}: {
  detail: BlueprintDetail;
  payload: VersionPayload | undefined;
}) {
  const terms = useTerms();
  const parts = [detail.blueprint.client.name, detail.blueprint.name];
  if (payload) parts.push(versionLabel(payload.version));
  if (payload?.version.status === 'draft') {
    parts.push(`Draft — visible to editors of this ${terms.noun('blueprint')} only`);
  }
  const text = parts.join(' · ');
  // The full text is the tooltip: the line is pinned to one row and truncates
  // with an ellipsis when the actions beside it leave too little room.
  return (
    <div className="status statusline" title={text}>
      {text}
    </div>
  );
}

/**
 * The unsaved-changes indicator (T3.8).
 *
 * Clicking a chip writes straight through, so "unsaved" is the short window
 * while a write is in flight, plus any write the API refused. It sits BESIDE
 * the status line, not inside it: the status line is pinned to one line and
 * this has its own fixed width, so a change of wording here can never move the
 * line or the page under the cursor.
 */
function SaveIndicator({ pending, saved, failed }: { pending: number; saved: number; failed: boolean }) {
  let text = '';
  let tone = '';
  if (pending > 0) {
    text = pending === 1 ? 'Saving…' : `Saving ${pending} changes…`;
  } else if (failed) {
    text = 'Not saved';
    tone = ' err';
  } else if (saved > 0) {
    text = saved === 1 ? '1 change saved' : `${saved} changes saved`;
  }
  return (
    <div className={`save-indicator${tone}`} role="status" aria-live="polite">
      {text}
    </div>
  );
}

/**
 * The preview bar (T9.1).
 *
 * The slider does not navigate and does not prompt, so this is the "visible way
 * back" that makes that safe: it says a past week is on screen, which week, and
 * what is still open underneath, and it offers one control to return to it.
 *
 * It sits between the blueprint bar and the views, NOT inside the sheet. Werner
 * was explicit that a carried-forward week gets no banner on the sheet — the
 * slider line already carries that badge, beside the control just moved. This
 * is a different thing: it is about the MODE, not about the week's figure, and
 * it is the only indication on the timeline tab, where there is no slider.
 */
function PreviewBar({
  preview,
  open,
  onLeave,
}: {
  preview: WeekPreview;
  /** The version still open underneath — what "back" goes back to. */
  open: VersionPayload;
  onLeave: () => void;
}) {
  const week = preview.row ? weekLabel(preview.row) : 'that week';
  const draft = open.version.status === 'draft';

  const what =
    preview.state === 'ready' && preview.payload
      ? `showing version ${preview.payload.version.versionNo}`
      : preview.state === 'blank'
        ? 'nothing had been published by then'
        : preview.state === 'error'
          ? 'could not be loaded'
          : 'still loading';

  return (
    <div className="bp-preview" role="status" aria-live="polite">
      <span className="bp-preview-tag">Preview</span>
      <span className="bp-preview-text">
        <b>{week}</b> — {what}. This is read-only;{' '}
        {draft
          ? `your draft is still open and nothing in it has changed.`
          : `version ${open.version.versionNo} is still open.`}
      </span>
      <button type="button" className="btn" onClick={onLeave}>
        {draft
          ? `Back to the draft`
          : `Back to version ${open.version.versionNo}`}
      </button>
    </div>
  );
}

/** Inside the provider, so the title and every word below it use resolved terminology. */
function Loaded({
  detail,
  payload,
  source,
  selectedId,
  onSelect,
  onPublished,
  preview,
  weekParam,
  onSelectWeek,
  onLeavePreview,
  movedOn,
  onRefused,
  onDismissMovedOn,
}: {
  detail: BlueprintDetail;
  payload: VersionPayload;
  source: PayloadSource;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onPublished: () => void;
  /** The previewed week, or null when the open version is what is rendered. */
  preview: WeekPreview | null;
  /** The raw `?week=` value, which the slider needs even before it resolves. */
  weekParam: string | null;
  onSelectWeek: (weekStart: string) => void;
  onLeavePreview: () => void;
  /** Held by the route, not here: see `onRefused` in useDraftEditor. */
  movedOn: MovedOn | null;
  onRefused: (versionId: string, edit: MovedOn['refused'][number], first: boolean) => void;
  onDismissMovedOn: () => void;
}) {
  const terms = useTerms();
  useDocumentTitle(detail.blueprint.name, terms.noun('blueprint'));

  const { blueprint } = detail;
  // Two conditions, both required: the caller may write here, and this version
  // is the draft. A published version is immutable however the caller is
  // permitted. For a viewer `editing` is false on every route, so the editing
  // controls below are not rendered — absent, not disabled.
  const editing = canEdit(blueprint.role) && payload.version.status === 'draft';

  /**
   * The editor is mounted on the OPEN version's cache entry and stays enabled
   * while a week is previewed. That is deliberate: the draft is still open
   * underneath, a write already in flight when the slider moved still settles
   * and reconciles into it, and nothing about the draft changes because a
   * historical week is on screen. What goes away is every control that could
   * start a new write — see `interactive` below.
   */
  const editor = useDraftEditor({
    queryKey: payloadKey(blueprint.id, source),
    versionId: payload.version.id,
    enabled: editing,
    onRefused,
  });

  // T9.2. Read-only means read-only, whatever the role: no click-to-cycle, no
  // upload, no publish while a past week is on screen. Absent from the DOM
  // rather than disabled, like every other read-only control in this app.
  const previewing = preview !== null;
  const interactive = editing && !previewing;

  // What is actually drawn. The open version except while previewing, where it
  // is the week's version — and UNDEFINED for a week with nothing to show,
  // which the views below replace with an empty state rather than zeros.
  const shown: VersionPayload | undefined = previewing ? preview.payload : payload;
  // Components that need a model to mount at all still get one; when `shown` is
  // undefined the sheet they would draw is replaced, so it is never seen.
  const rendered = shown ?? payload;

  // T7.24: Uploaded Documents replaced Architecture and exists on every
  // blueprint, so there is no longer a tab to hide and nowhere to redirect from.
  // The legacy_architecture column and every reference to it went with T7.6.
  const { view } = useTheme();

  return (
    <>
      <ViewTabs />
      <div className="bp-bar">
        <div className="grow">
          <h2>{blueprint.name}</h2>
          {/* What is RENDERED, not what is open: during a preview the sheet
              below is the week's version and the line must not label it as the
              draft. The preview bar immediately underneath says the draft is
              still open. One line still, as ever. */}
          <StatusLine detail={detail} payload={shown} />
        </div>
        {editing ? (
          <SaveIndicator pending={editor.pending} saved={editor.saved} failed={Boolean(editor.failure)} />
        ) : null}
        {/* The dropdown names what is RENDERED, so while a week is previewed it
            shows that week's version rather than the one still open underneath;
            the preview bar below says which is which. Choosing from it leaves
            the preview, which is how the two controls agree (T9.6). */}
        <VersionSelector
          versions={detail.versions}
          role={blueprint.role}
          selectedId={preview?.payload?.version.id ?? selectedId ?? payload.version.id}
          onSelect={onSelect}
        />
        <div className="bp-actions">
          {blueprint.role === 'admin' ? (
            <Link className="btn" to={`/admin/blueprints/${blueprint.id}`}>
              {terms.noun('blueprint')} settings
            </Link>
          ) : null}
          {interactive ? (
            <>
              <UploadDialog
                blueprintId={blueprint.id}
                replacesContent
                pendingWrites={editor.pending}
              />
              <PublishButton
                blueprintId={blueprint.id}
                versionNo={payload.version.versionNo}
                pendingWrites={editor.pending}
                onPublished={onPublished}
              />
            </>
          ) : null}
          {/* What you see is what you download. During a preview that is the
              week's version, not the one open underneath. A blank week has no
              version at all, so there is nothing to offer. */}
          {shown ? (
            <DownloadButton versionId={shown.version.id} versionNo={shown.version.versionNo} />
          ) : null}
        </div>
      </div>

      {preview ? (
        <PreviewBar preview={preview} open={payload} onLeave={onLeavePreview} />
      ) : null}

      <div className="bp-toasts">
        {movedOn ? (
          <div className="bp-toast" role="alert">
            <span>
              <b>
                This {terms.noun('blueprint')} was published while you were editing, so this screen
                has moved to the new draft.
              </b>{' '}
              {movedOn.refused.length === 1 ? 'This change was' : 'These changes were'} made
              on the old version and not saved:{' '}
              {movedOn.refused.map((r, i) => (
                <span key={r.index}>
                  {i > 0 ? '; ' : ''}
                  {r.name} to {terms.state(stateTermKey(r.state))}
                </span>
              ))}
              . Make {movedOn.refused.length === 1 ? 'it' : 'them'} again if you still want{' '}
              {movedOn.refused.length === 1 ? 'it' : 'them'}.
            </span>
            <button type="button" className="btn" onClick={onDismissMovedOn}>
              Dismiss
            </button>
          </div>
        ) : null}
        {editor.failure ? (
          <div className="bp-toast" role="alert">
            <span>
              <b>Not saved.</b> {editor.failure.name}: {editor.failure.message}
            </span>
            <button type="button" className="btn" onClick={editor.dismissFailure}>
              Dismiss
            </button>
          </div>
        ) : null}
      </div>

      {view === 'fp' ? (
        <BlueprintView
          model={rendered.model}
          schedule={rendered.schedule}
          // T7.26. The weekly progress slider asks the API for this
          // blueprint's week series, so it needs the id. Unconditional: the
          // series is readable by anyone who can see the blueprint, editor or
          // not, and a viewer watching progress by week is the main audience
          // for it.
          blueprintId={blueprint.id}
          // T9.1-T9.5. The slider's committed position and what the sheet
          // should be. Both come from the URL by way of the route, so the
          // control and the sheet cannot disagree.
          week={{
            committedWeek: weekParam,
            committedVersionId: selectedId,
            onCommit: onSelectWeek,
            preview: preview ? preview.state : 'none',
          }}
          {...(interactive ? { onCycle: editor.cycle } : {})}
        />
      ) : view === 'tl' ? (
        shown ? (
        <TimelineView
          model={rendered.model}
          schedule={rendered.schedule}
          holidays={rendered.holidays}
          leave={rendered.leave}
          // T7.31. Present only while a draft is open to an editor, which is
          // what makes the timeline read-only everywhere else — the same
          // condition the Blueprint view's click-to-cycle uses, so the two
          // cannot disagree about whether this version is editable. A previewed
          // week is read-only too, so `interactive` and not `editing`.
          {...(interactive
            ? { editing: { versionId: payload.version.id, queryKey: payloadKey(blueprint.id, source) } }
            : {})}
        />
        ) : (
          // The timeline is version content, so a previewed week with no version
          // has no timeline either. Same rule as the sheet: say so, rather than
          // draw the open version's and let it pass for the week's.
          <div className="empty" role="status">
            {preview?.state === 'blank'
              ? `Nothing had been published by this week, so there is no timeline to show.`
              : preview?.state === 'error'
                ? 'Could not load that week.'
                : 'Loading…'}
          </div>
        )
      ) : (
        // Documents belong to the blueprint, not to a version, so this takes the
        // blueprint id and not the payload. `canEdit` alone, without the draft
        // condition the other two views use: reference material is not version
        // content, so an editor looking at a published version still manages it.
        // For a viewer it is false, and every editing control is then absent
        // from the DOM rather than disabled.
        <DocumentsView blueprintId={blueprint.id} canEdit={canEdit(blueprint.role)} />
      )}
    </>
  );
}

/** What the route is previewing, if anything. See the header note. */
export interface WeekPreview {
  /** The Monday in the address. */
  weekStart: string;
  /** Null only while the series itself is still arriving. */
  row: WeekSeriesRow | null;
  /** Absent for a blank week, and while the version is being fetched. */
  payload: VersionPayload | undefined;
  state: 'loading' | 'ready' | 'blank' | 'error';
}

export function BlueprintRoute() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const versionId = params.get('version');
  const weekParam = params.get('week');

  const [movedOn, setMovedOn] = useState<MovedOn | null>(null);

  // A blueprint opens on its Blueprint tab, and leaving returns the bar to the
  // Blueprint theme, so the picker and admin screens are never themed for a tab.
  const { setView } = useTheme();
  useEffect(() => {
    setView('fp');
    return () => setView('fp');
  }, [id, setView]);

  const detailQuery = useBlueprint(id);

  // An editor opens the draft (design.md § 6); a viewer, or an editor with no
  // draft, opens the latest published version. The choice needs the version
  // list, so the payload request waits for it rather than guessing.
  const detail = detailQuery.data;
  const source: PayloadSource = versionId
    ? { kind: 'version', versionId }
    : detail && canEdit(detail.blueprint.role) && detail.versions.some((v) => v.status === 'draft')
      ? { kind: 'draft' }
      : { kind: 'latest' };
  const hasVersions = Boolean(detail && detail.versions.length > 0);
  // Unconditional on the preview: this is the version that stays OPEN
  // underneath, and keeping its query mounted is what keeps an editor's draft —
  // and the write-through editor built on its cache entry — alive while a past
  // week is on screen.
  const payloadQuery = useVersionPayload(id, source, hasVersions);

  /* ------------------------------------------------------- the week preview */

  // The same series the slider reads, resolved here because the URL decides
  // what is rendered. React Query serves both callers from one cache entry, so
  // this is not a second request.
  const seriesQuery = useWeekSeries(id, hasVersions);
  const weeks = useMemo(
    () => (seriesQuery.data ? weeksToDate(seriesQuery.data, ymd(mondayOf(new Date()))) : []),
    [seriesQuery.data],
  );
  const previewRow = weeks[indexOfWeek(weeks, weekParam)];

  // T9.4: the series row for a week with no version of its own already carries
  // the latest previous version's id, so "show the previous one" is read from
  // the series rather than invented here. T9.5: a week before any version has
  // none to carry, and that is the blank case below — never a model of zeros.
  const previewVersionId = previewRow?.versionId ?? null;
  const previewQuery = useVersionPayload(
    id,
    { kind: 'version', versionId: previewVersionId ?? '' },
    Boolean(previewVersionId),
  );

  const preview: WeekPreview | null = !weekParam
    ? null
    : !previewRow
      ? // Either the series has not arrived yet or the address names a week
        // outside it. Both show the open version until it resolves; an
        // unresolvable week simply never becomes a preview.
        seriesQuery.isPending
        ? { weekStart: weekParam, row: null, payload: undefined, state: 'loading' }
        : null
      : !previewVersionId
        ? { weekStart: weekParam, row: previewRow, payload: undefined, state: 'blank' }
        : previewQuery.data
          ? { weekStart: weekParam, row: previewRow, payload: previewQuery.data, state: 'ready' }
          : previewQuery.isError
            ? { weekStart: weekParam, row: previewRow, payload: undefined, state: 'error' }
            : { weekStart: weekParam, row: previewRow, payload: undefined, state: 'loading' };

  // Choosing a version leaves the preview: the dropdown names what is OPEN, and
  // the slider follows it to that version's week rather than the two fighting
  // over what is on screen (T9.6).
  const selectVersion = (next: string) => {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set('version', next);
      out.delete('week');
      return out;
    });
  };

  // One history entry per gesture. The guard is here rather than in the slider
  // because it is the URL that must not be written twice: a pointerup and the
  // re-render after it can both report the same week.
  const selectWeek = (weekStart: string) => {
    if (weekStart === weekParam) return;
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set('week', weekStart);
      return out;
    });
  };

  const leavePreview = () => {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.delete('week');
      return out;
    });
  };

  // After a publish the address must stop naming the version just published,
  // or the editor would be left looking at an immutable copy of what they made.
  const leaveVersion = () => {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.delete('version');
      out.delete('week');
      return out;
    });
  };

  // Held here rather than in the editor: leaving ?version= swaps the payload
  // query, the "Loading…" branch below unmounts the editor, and a notice kept in
  // its state would vanish with it.
  const onRefused = (refusedVersion: string, edit: MovedOn['refused'][number], first: boolean) => {
    setMovedOn((prev) => {
      const same = prev && prev.versionId === refusedVersion ? prev.refused : [];
      return {
        versionId: refusedVersion,
        refused: [...same.filter((r) => r.index !== edit.index), edit],
      };
    });
    if (first) leaveVersion();
  };

  if (detailQuery.isPending || (hasVersions && payloadQuery.isPending)) {
    return (
      <div className="empty" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return <LoadFailure error={detailQuery.error} />;
  }

  // The blueprint exists but has never been published, and the caller may not
  // see a draft (or there is none). A 200 with no versions is the normal answer.
  if (detailQuery.data.versions.length === 0) {
    return (
      <TermsProvider terminology={detailQuery.data.terms}>
        <EmptyBlueprint detail={detailQuery.data} />
      </TermsProvider>
    );
  }

  if (payloadQuery.isError || !payloadQuery.data) {
    return (
      <TermsProvider terminology={detailQuery.data.terms}>
        <LoadFailure error={payloadQuery.error} />
      </TermsProvider>
    );
  }

  return (
    <TermsProvider terminology={payloadQuery.data.terms}>
      <Loaded
        detail={detailQuery.data}
        payload={payloadQuery.data}
        source={source}
        selectedId={versionId}
        onSelect={selectVersion}
        onPublished={leaveVersion}
        preview={preview}
        weekParam={weekParam}
        onSelectWeek={selectWeek}
        onLeavePreview={leavePreview}
        movedOn={movedOn}
        onRefused={onRefused}
        onDismissMovedOn={() => setMovedOn(null)}
      />
    </TermsProvider>
  );
}

function EmptyBlueprint({ detail }: { detail: BlueprintDetail }) {
  const terms = useTerms();
  const { blueprint } = detail;
  return (
    <div className="empty">
      <b>{blueprint.name}</b>
      <br />
      This {terms.noun('blueprint')} has no version you can see yet.
      {canEdit(blueprint.role) ? (
        <div className="bp-empty-action">
          <UploadDialog blueprintId={blueprint.id} replacesContent={false} pendingWrites={0} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Failure states, and why 404 is deliberately uninformative here.
 *
 * design.md D-21: a viewer asking for a draft by id gets 404, NOT 403, and that
 * 404 is byte-identical to the one an admin gets for a blueprint that genuinely
 * has no draft. A 403 would confirm that unpublished work exists at that id,
 * which turns id-guessing into a working oracle for exactly the fact D-2
 * withholds.
 *
 * So this component must not try to tell those two cases apart, and must not
 * mention drafts in the 404 message. There is deliberately nothing there to
 * distinguish, and a message that hinted otherwise would give away by wording
 * what the status code was careful not to give away.
 *
 * In practice a viewer rarely reaches this: drafts are absent from their version
 * list and `hasDraft` is false on their picker row, so the only routes here are
 * a stale bookmark or a hand-typed id.
 */
function LoadFailure({ error }: { error: unknown }) {
  const terms = useTerms();

  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="empty" role="alert">
        That version is not available.
        <br />
        Open the {terms.noun('blueprint')} without a version in the address to see the current
        live one.
      </div>
    );
  }
  // 403 does not arise on the read path today; it is here for the editing
  // routes in Phase 3. It says nothing about what exists, only about this user.
  if (error instanceof ApiError && error.isForbidden) {
    return (
      <div className="empty" role="alert">
        You do not have permission to do that.
      </div>
    );
  }
  return (
    <div className="empty" role="alert">
      Could not load this {terms.noun('blueprint')}.
      <br />
      {error instanceof Error ? error.message : 'Try again in a moment.'}
    </div>
  );
}
